import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import * as api from "../../constants/api_constants";
import {
  getAllCountries,
  getCitiesForState,
  getStatesForCountry,
  getCountryByCode,
} from "helpers/utils/locationUtil";
import {
  charactersWithSpaces,
  identificationNumber,
  zipcode,
} from "helpers/validations/regex";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import secureLocalStorage from "react-secure-storage";
import { get, post, put } from "helpers/api_helper";

function AddVendor() {
  const navigate = useNavigate();
  const { vendorId } = useParams();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const initialVendorState = {
    company: "",
    vatNumber: "",
    website: "",
    phone: "",
    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    primaryContactDesignation: "",
    paymentTerms: "",
    currency: "",
    country: "",
    province: "",
    zipcode: "",
    city: "",
    address: "",
    isNavigate: false,
  };

  const [fetchedVendor, setFetchedVendor] = useState(initialVendorState);
  const storedUser = secureLocalStorage.getItem("authUser");
  const user = JSON.parse(storedUser || "{}");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const fetchedCountries = getAllCountries();
        setCountries(fetchedCountries);
        if (vendorId) {
          await fetchVendorDetails();
        }
      } catch (error) {
        const errorMessage = "Failed to fetch location data";
        console.error(errorMessage, error);
        showErrorToast(errorMessage);
        setApiError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [vendorId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: fetchedVendor,
    validationSchema: Yup.object({
      company: Yup.string()
        .required("Company name is required")
        .matches(
          charactersWithSpaces,
          "Company name should only contain characters and spaces"
        ),
      vatNumber: Yup.string()
        .required("VAT number is required")
        .matches(
          identificationNumber,
          "VAT number should only contain alphanumeric characters"
        ),
      primaryContactName: Yup.string()
        .required("Contact name is required")
        .matches(
          charactersWithSpaces,
          "Contact name should only contain characters and spaces"
        ),
      primaryContactPhone: Yup.string()
        .required("Primary contact phone is required")
        .test(
          "isValidPhone",
          "Invalid phone number format",
          (value) => !value || isValidPhoneNumber(value)
        ),
      website: Yup.string().url("Invalid URL format"),
      primaryContactEmail: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      phone: Yup.string().test(
        "isValidPhone",
        "Invalid phone number format",
        (value) => !value || isValidPhoneNumber(value)
      ),
      primaryContactDesignation: Yup.string().matches(
        charactersWithSpaces,
        "Designation should only contain characters and spaces"
      ),
      country: Yup.string().required("Country is required"),
      currency: Yup.string().required("Currency is required"),
      paymentTerms: Yup.number()
        .min(0, "Payment terms cannot be negative")
        .required("Payment terms is required"),
      province: Yup.string().required("Province is required"),
      zipcode: Yup.string()
        .required("Zip code is required")
        .matches(zipcode, "Zip code must be 4-8 characters long"),
      city: Yup.string().required("City is required"),
      address: Yup.string(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        vendorDto: {
          subscriptionId: 100,
          companyName: values.company,
          vendorCode: values.vendorCode || undefined,
          vat: values.vatNumber,
          currency: values.currency,
          website: values.website || undefined,
          designation: values.primaryContactDesignation || undefined,
          status: 1,
          primaryPhone: values.primaryContactPhone,
          primaryContactName: values.primaryContactName,
          address: values.address || undefined,
          city: values.city,
          province: values.province,
          zipCode: values.zipcode,
          country: values.country,
          email: values.primaryContactEmail,
          phone: values.phone || undefined,
          paymentTerms: Number(values.paymentTerms),
          createdBy: user?.email || "unknown",
          updatedBy: user?.email || "unknown",
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
        },
      };

      setIsLoading(true);
      try {
        let response;
        if (vendorId) {
          response = await put(api.UPDATE_VENDOR_DATA_BY_ID(vendorId), payload);
        } else {
          response = await post(api.CREATE_VENDOR, payload);
        }

        console.log("API Response:", JSON.stringify(response, null, 2));

        const newVendorId = response?.data?.vendorId || vendorId;
        if (!newVendorId) {
          throw new Error("Vendor ID not found in response");
        }

        showSuccessToast(
          vendorId ? "Vendor updated successfully" : "Vendor created successfully"
        );

        const vendorData = {

          
          company: values.company,
          vatNumber: values.vatNumber,
          website: values.website,
          phone: values.phone,
          primaryContactName: values.primaryContactName,
          primaryContactEmail: values.primaryContactEmail,
          primaryContactPhone: values.primaryContactPhone,
          primaryContactDesignation: values.primaryContactDesignation,
          currency: values.currency,
          country: values.country,
          province: values.province,
          zipcode: values.zipcode,
          city: values.city,
          address: values.address,
          paymentTerms: values.paymentTerms,
        };

        // Store vendor data in secureLocalStorage for AddVendorShippingBilling
        secureLocalStorage.setItem(
          "pendingVendorData_new",
          JSON.stringify(vendorData)
        );

        if (values.isNavigate) {
          const navState = {
            vendorId: newVendorId,
            vendorData: {
              companyName: values.company,
              zipcode: values.zipcode,
              vat: values.vatNumber,
              country: values.country,
              currency: values.currency,
              province: values.province, // Include additional address fields
              city: values.city,
              address: values.address,
            },
          };
          debugger
          console.log("Navigating to /addVendorShippingBilling with state:", navState);
          navigate("/addVendorShippingBilling", { state: navState });
        } else {
          navigate("/vendors");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          (vendorId ? "Failed to update vendor" : "Failed to create vendor");
        console.error("API Error:", errorMessage, error);
        showErrorToast(errorMessage);
        setApiError(errorMessage);
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (fetchedVendor.country) {
      const fetchedStates = getStatesForCountry(fetchedVendor.country);
      setStates(fetchedStates);
      if (fetchedVendor.province) {
        const fetchedCities = getCitiesForState(
          fetchedVendor.country,
          fetchedVendor.province
        );
        setCities(fetchedCities);
      }
    }
  }, [fetchedVendor]);

  const fetchVendorDetails = async () => {
    try {
      const response = await get(api.GET_VENDOR_BY_ID(vendorId));
      console.log("Fetch Vendor Response:", JSON.stringify(response, null, 2));
      const vendData = response?.data?.data?.[0] || response?.data?.[0];
      if (vendData) {
        setFetchedVendor({
          company: vendData.companyName || "",
          vatNumber: vendData.vat || "",
          website: vendData.website || "",
          phone: vendData.phone || "",
          primaryContactName: vendData.primaryContactName || "",
          primaryContactEmail: vendData.email || "",
          primaryContactPhone: vendData.primaryPhone || "",
          primaryContactDesignation: vendData.designation || "",
          paymentTerms: vendData.paymentTerms || "",
          currency: vendData.currency || "",
          country: vendData.country || "",
          province: vendData.province || "",
          zipcode: vendData.zipCode || "",
          city: vendData.city || "",
          address: vendData.address || "",
          isNavigate: false,
        });
      } else {
        throw new Error("No vendor data found");
      }
    } catch (error) {
      const errorMessage = error.message || "Error fetching vendor details";
      console.error(errorMessage, error);
      setApiError(errorMessage);
      showErrorToast(errorMessage);
    }
  };

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    validation.setFieldValue("country", selectedCountry);
    const countryDetails = getCountryByCode(selectedCountry);
    validation.setFieldValue("currency", countryDetails?.currency || "");
    const fetchedStates = getStatesForCountry(selectedCountry);
    setStates(fetchedStates);
    setCities([]);
    validation.setFieldValue("province", "");
    validation.setFieldValue("city", "");
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    validation.setFieldValue("province", selectedState);
    const fetchedCities = getCitiesForState(
      validation.values.country,
      selectedState
    );
    setCities(fetchedCities);
    validation.setFieldValue("city", "");
  };

  const handleAddress = () => {
    console.log("handleAddress triggered, setting isNavigate to true");
    validation.setFieldValue("isNavigate", true);
    validation.handleSubmit();
  };

  const handleSave = () => {
    validation.setFieldValue("isNavigate", false);
    validation.handleSubmit();
  };

  return (
    <div className="page-content">
      <Container fluid style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading={vendorId ? "Edit Vendor" : "Add Vendor"}
          routePath="Home"
          pageName="Vendors"
        />
        <Row className="p-4">
          <Col>
            <Card>
              <CardBody>
                {isLoading ? (
                  <div>Loading...</div>
                ) : (
                  <Form
                    id="vendorForm"
                    className="row g-3 needs-validation py-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      console.log("Form submitted, validation errors:", validation.errors);
                      validation.handleSubmit();
                    }}
                  >
                    <Row className="justify-content-start">
                      <Col xs={12} className="card-header">
                        <p className="form-header">Vendor Information</p>
                      </Col>
                      {/* Form fields remain unchanged */}
                      <Col md={4} className="mb-3">
                        <Label htmlFor="company" className="col-form-label">
                          Company Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          className="form-control"
                          placeholder="Company name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.company}
                          invalid={
                            validation.touched.company &&
                            validation.errors.company
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.company &&
                          validation.errors.company && (
                            <FormFeedback>
                              {validation.errors.company}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="vatNumber" className="col-form-label">
                          VAT / GST <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="vatNumber"
                          name="vatNumber"
                          type="text"
                          className="form-control"
                          placeholder="VAT/GST"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.vatNumber}
                          invalid={
                            validation.touched.vatNumber &&
                            validation.errors.vatNumber
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.vatNumber &&
                          validation.errors.vatNumber && (
                            <FormFeedback>
                              {validation.errors.vatNumber}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="phone" className="col-form-label">
                          Phone
                        </Label>
                        <PhoneInput
                          international
                          defaultCountry="IN"
                          id="phone"
                          name="phone"
                          className="form-control d-flex"
                          placeholder="Enter phone number"
                          onChange={(value) =>
                            validation.setFieldValue("phone", value)
                          }
                          onBlur={validation.handleBlur}
                          value={validation.values.phone}
                          disabled={isLoading}
                        />
                        {validation.touched.phone &&
                          validation.errors.phone && (
                            <FormFeedback>
                              {validation.errors.phone}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="website" className="col-form-label">
                          Website
                        </Label>
                        <Input
                          id="website"
                          name="website"
                          type="text"
                          className="form-control"
                          placeholder="URL"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.website}
                          invalid={
                            validation.touched.website &&
                            validation.errors.website
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.website &&
                          validation.errors.website && (
                            <FormFeedback>
                              {validation.errors.website}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label
                          htmlFor="primaryContactName"
                          className="col-form-label"
                        >
                          Primary Contact Name{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="primaryContactName"
                          name="primaryContactName"
                          type="text"
                          className="form-control"
                          placeholder="Enter name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.primaryContactName}
                          invalid={
                            validation.touched.primaryContactName &&
                            validation.errors.primaryContactName
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.primaryContactName &&
                          validation.errors.primaryContactName && (
                            <FormFeedback>
                              {validation.errors.primaryContactName}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label
                          htmlFor="primaryContactEmail"
                          className="col-form-label"
                        >
                          Primary Contact Email{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="primaryContactEmail"
                          name="primaryContactEmail"
                          type="email"
                          className="form-control"
                          placeholder="Enter email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.primaryContactEmail}
                          invalid={
                            validation.touched.primaryContactEmail &&
                            validation.errors.primaryContactEmail
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.primaryContactEmail &&
                          validation.errors.primaryContactEmail && (
                            <FormFeedback>
                              {validation.errors.primaryContactEmail}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label
                          htmlFor="primaryContactPhone"
                          className="col-form-label"
                        >
                          Primary Contact Phone{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <PhoneInput
                          international
                          defaultCountry="IN"
                          id="primaryContactPhone"
                          name="primaryContactPhone"
                          className="form-control d-flex"
                          placeholder="Enter phone number"
                          onChange={(value) =>
                            validation.setFieldValue(
                              "primaryContactPhone",
                              value
                            )
                          }
                          onBlur={validation.handleBlur}
                          value={validation.values.primaryContactPhone}
                          disabled={isLoading}
                        />
                        {validation.touched.primaryContactPhone &&
                          validation.errors.primaryContactPhone && (
                            <FormFeedback>
                              {validation.errors.primaryContactPhone}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label
                          htmlFor="primaryContactDesignation"
                          className="col-form-label"
                        >
                          Designation
                        </Label>
                        <Input
                          id="primaryContactDesignation"
                          name="primaryContactDesignation"
                          type="text"
                          className="form-control"
                          placeholder="Enter designation"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.primaryContactDesignation}
                          invalid={
                            validation.touched.primaryContactDesignation &&
                            validation.errors.primaryContactDesignation
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.primaryContactDesignation &&
                          validation.errors.primaryContactDesignation && (
                            <FormFeedback>
                              {validation.errors.primaryContactDesignation}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="country" className="col-form-label">
                          Country <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="country"
                          name="country"
                          type="select"
                          className="form-control"
                          onChange={handleCountryChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.country}
                          invalid={
                            validation.touched.country &&
                            validation.errors.country
                          }
                          disabled={isLoading}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.value} value={country.value}>
                              {country.name}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.country &&
                          validation.errors.country && (
                            <FormFeedback>
                              {validation.errors.country}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="currency" className="col-form-label">
                          Currency <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="currency"
                          name="currency"
                          type="text"
                          className="form-control"
                          placeholder="Currency"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.currency}
                          invalid={
                            validation.touched.currency &&
                            validation.errors.currency
                          }
                          disabled
                        />
                        {validation.touched.currency &&
                          validation.errors.currency && (
                            <FormFeedback>
                              {validation.errors.currency}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label
                          htmlFor="paymentTerms"
                          className="col-form-label"
                        >
                          Payment Terms (Days){" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="paymentTerms"
                          name="paymentTerms"
                          type="number"
                          className="form-control"
                          placeholder="Payment terms"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.paymentTerms}
                          invalid={
                            validation.touched.paymentTerms &&
                            validation.errors.paymentTerms
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.paymentTerms &&
                          validation.errors.paymentTerms && (
                            <FormFeedback>
                              {validation.errors.paymentTerms}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="province" className="col-form-label">
                          Province <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="province"
                          name="province"
                          type="select"
                          className="form-control"
                          onChange={handleStateChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.province}
                          invalid={
                            validation.touched.province &&
                            validation.errors.province
                          }
                          disabled={isLoading}
                        >
                          <option value="">Select Province</option>
                          {states.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.label}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.province &&
                          validation.errors.province && (
                            <FormFeedback>
                              {validation.errors.province}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="city" className="col-form-label">
                          City <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="city"
                          name="city"
                          type="select"
                          className="form-control"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.city}
                          invalid={
                            validation.touched.city && validation.errors.city
                          }
                          disabled={isLoading}
                        >
                          <option value="">Select City</option>
                          {cities.map((city) => (
                            <option key={city.value} value={city.value}>
                              {city.label}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.city && validation.errors.city && (
                          <FormFeedback>{validation.errors.city}</FormFeedback>
                        )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="zipcode" className="col-form-label">
                          Zip Code <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="zipcode"
                          name="zipcode"
                          type="text"
                          className="form-control"
                          placeholder="Enter zip code"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.zipcode}
                          invalid={
                            validation.touched.zipcode &&
                            validation.errors.zipcode
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.zipcode &&
                          validation.errors.zipcode && (
                            <FormFeedback>
                              {validation.errors.zipcode}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label htmlFor="address" className="col-form-label">
                          Address
                        </Label>
                        <Input
                          type="textarea"
                          id="address"
                          name="address"
                          maxLength="225"
                          rows="3"
                          className="form-control"
                          placeholder="Enter address"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.address}
                          invalid={
                            validation.touched.address &&
                            validation.errors.address
                          }
                          disabled={isLoading}
                        />
                        {validation.touched.address &&
                          validation.errors.address && (
                            <FormFeedback>
                              {validation.errors.address}
                            </FormFeedback>
                          )}
                      </Col>
                    </Row>
                  </Form>
                )}
                {apiError && (
                  <div className="alert alert-danger mt-3">{apiError}</div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Footer
          save={handleSave}
          buttons={[
            {
              text: vendorId
                ? "Edit Billing/Shipping"
                : "Save and Add Billing/Shipping",
              color: "primary",
              onClick: handleAddress,
              disabled: isLoading,
            },
          ]}
          disabled={isLoading}
        />
      </Container>
    </div>
  );
}

export default AddVendor;